import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useSelector } from 'react-redux';
import './UserOrders.css';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const UserOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const user = useSelector((state) => state.auth.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/loginK');
      setOrders([]); // Clear orders if no user
      return;
    }

    const fetchOrders = async () => {
      console.log('Fetching orders for user:', user.username); // Log username

      setLoading(true); // Start loading
      setError(null); // Clear any previous errors

      try {
        const ordersCollection = collection(db, 'orders');
        const q = query(ordersCollection, where('user', '==', user.username));
        const ordersSnapshot = await getDocs(q);

        const ordersList = ordersSnapshot.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        });

        setOrders(ordersList);

        // Filter approved orders ready for collection
        const readyOrders = ordersList.filter(
          (order) => order.status === 'approved' && order.collectionDate
        );

        if (readyOrders.length > 0) {
          showApprovedOrdersAlert(readyOrders);
        }
      } catch (err) {
        setError('Failed to fetch orders. Please try again.');
      } finally {
        setLoading(false); // Stop loading after fetching data
      }
    };

    fetchOrders();
  }, [user, navigate]);

  const changeBodyBackground = (background) => {
    document.body.style.background = background;
  };

  useEffect(() => {
    changeBodyBackground('linear-gradient(to bottom, #9aa3e5, rgba(213, 213, 187, 0))');
    return () => changeBodyBackground(''); // Reset background on unmount
  }, []);

  const handleCancelOrder = async (orderId) => {
    try {
      const orderDocRef = doc(db, 'orders', orderId);
      await deleteDoc(orderDocRef);
      setOrders(orders.filter((order) => order.id !== orderId));
    } catch (err) {
      setError('Failed to cancel order. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    if (timestamp?.seconds) {
      const date = new Date(timestamp.seconds * 1000); // Convert seconds to milliseconds
      const formattedDate = date.toLocaleDateString() + ' ' + date.toLocaleTimeString(); // Format as date and time
      return formattedDate;
    }
    return '';
  };

  const getOrderBackgroundColor = (status) => {
    console.log('Getting background color for status:', status); // Log status
    switch (status) {
      case 'waiting for the warehouse':
        return '#d1ecf1'; // Light blue
      case 'approved':
        return '#d4edda'; // Green
      case 'new':
        return '#f8d7da'; // Light red
      default:
        return '#f8d7da'; // Red
    }
  };

  const calculateCollectionTag = (collectionDate) => {
    if (!collectionDate) return null;
    const collectionDateObj = new Date(collectionDate);
    const today = new Date();
    const diffInDays = (collectionDateObj - today) / (1000 * 60 * 60 * 24);

    if (diffInDays < 0) {
      if (Math.abs(diffInDays) > 3) return 'פג תוקף'; // More than 3 days passed
      else return 'פג תוקף בקרוב';
    }
    if (diffInDays === 0) return 'היום'; // Collect today
    return `זמין לאיסוף מ: ${collectionDate}`; // Display the original collection date
  };

  const showApprovedOrdersAlert = (approvedOrders) => {
    const orderDetails = approvedOrders.map((order) => ({
      html: `
        <div style="margin-bottom: 20px; text-align: right; direction: rtl; background-color: #ffffff; padding: 15px; border-radius: 12px; box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); border-left: 5px solid #1976d2;">
          <p style="font-size: 18px; font-weight: bold; color: #333;">מספר אישור: 
            <span style="font-size: 18px; color: #1976d2;">${order.confirmationNumber}</span>
            <button aria-label="copy" onclick="navigator.clipboard.writeText('${order.confirmationNumber}')" style="margin-left: 5px; color:black; background-color: transparent; padding: 4px; border-radius: 50%; cursor: pointer; border: none;">
              📋  העתק
            </button>
          </p>
          <p style="font-size: 14px; color: #666;"><strong>מוצרים:</strong> ${order.items.map((item) => `${item.name} (${item.quantity})`).join(', ')}</p>
          <p style="font-size: 14px; color: #666;"><strong>זמין לאיסוף מ:</strong> ${formatDate(order.collectionDate)}</p>
        </div>
      `,
    }));

    MySwal.fire({
      title: '<span style="font-size: 22px; color: #1976d2; font-weight: bold;">הזמנות מוכנות לאיסוף</span>',
      html: `<div id="yyy" style="max-height: 300px; overflow-y: auto; direction: rtl; text-align: right;">${orderDetails.map(detail => detail.html).join('')}</div>`,
      confirmButtonText: 'סגור',
      icon: 'info',
      customClass: {
        popup: 'swal2-popup-custom',
        confirmButton: 'swal2-confirm swal2-styled',
      },
      backdrop: 'rgba(0,0,0,0.4)',
      padding: '25px',
      width: '450px',
    });
  };

  if (loading) {
    return <div>Loading orders...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  console.log('Rendering orders. Total orders:', orders.length); // Log number of orders before rendering

  return (
    <div className="orders-container">
      <h1>ההזמנות שלך</h1>
      {orders.length === 0 ? (
        <p>No orders found.</p>
      ) : (
        <ul className="orders-list">
          {orders.map((order) => (
            <li key={order.id} className="order-item">
              <div className="order-card">
                {order.status === 'approved' && order.collectionDate && (
                  <div className={`collection-date-tag ${calculateCollectionTag(order.collectionDate)}`}>
                    {calculateCollectionTag(order.collectionDate)}
                  </div>
                )}
                <div className="order-table-header">
                  <div className="order-status" style={{ backgroundColor: getOrderBackgroundColor(order.status) }}>
                    {order.status}
                  </div>
                  <div className="order-date">{formatDate(order.orderDate)}</div>
                </div>
                <div className="order-items">
                  <h4 className="items-title">מוצרים:</h4>
                  {order.items && order.items.length > 0 ? (
                    <ul className="order-items-list">
                      {order.items.map((item, index) => (
                        <li key={index} className="order-item-detail">
                          <p><strong>שם:</strong> {item.name}</p>
                          <p><strong>כמות:</strong> {item.quantity}</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>No items in this order.</p>
                  )}
                </div>
                <button className="cancel-button" onClick={() => handleCancelOrder(order.id)}>
                  ביטול הזמנה
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default UserOrders;
